import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  commissionsApi,
  type AdjustmentType,
  type CommissionClaimDetailDto,
  type PaymentMethod,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { WHOLE_CENTS_MESSAGE, hasFractionOfACent } from "./wholeCents";

interface Props {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

const METHODS: PaymentMethod[] = ["Check", "ACH", "Wire", "Payroll", "Other"];
const TYPES: Array<{ value: AdjustmentType; label: string; blurb: string }> = [
  {
    value: "Correction",
    label: "Correction",
    blurb: "Recalculation — sign preserved. Use a negative amount to decrease TotalPaid.",
  },
  {
    value: "Clawback",
    label: "Clawback",
    blurb: "Deal fell through. Recovers funds; amount is entered positive and applied negative.",
  },
  {
    value: "Supplemental",
    label: "Supplemental",
    blurb: "Additional payment owed. Amount is entered positive and adds to TotalPaid.",
  },
];

export function AdjustmentModal({ open, onClose, claim }: Props) {
  const qc = useQueryClient();
  const tz = useUserTimeZone();
  const [type, setType] = useState<AdjustmentType>("Correction");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("ACH");
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(() => isoDateInTz(new Date(), tz));
  const [error, setError] = useState<string | null>(null);

  const selected = TYPES.find((t) => t.value === type)!;
  const amountNum = Number(amount) || 0;
  // The server refuses a fraction of a cent; the dialog says so first.
  const subCent = hasFractionOfACent(amountNum);
  const canSubmit =
    date !== "" &&
    amountNum !== 0 &&
    !subCent &&
    reason.trim().length > 0 &&
    reference.trim().length > 0;

  // 🔴 NOON UTC, NOT MIDNIGHT. `new Date(date).toISOString()` stored this
  // wall-calendar day at UTC midnight, which every tz-aware renderer then drew
  // as the PREVIOUS day for any reader west of UTC. This file held one of the
  // eight `BLOCKED_BASELINE` entries in `calendarDateWrites`, and the block was
  // real: `AdminCommissionService`'s old `<= dt` upper bound would have EXCLUDED
  // a noon value on the range's own last day. `CRIT-29` made that predicate
  // half-open (`p.PaymentDate < dayAfter`, `AdminCommissionService:472`), so the
  // blocker is gone and the entry is DELETED rather than decremented.
  //
  // The guard is not dead code. Neither date input is `required` and `canSubmit`
  // did not consult the date, so an emptied field reached `new Date("")` and
  // threw `RangeError: Invalid time value` from inside the mutation. `canSubmit`
  // now disables the button, and this fails with a sentence if anything else
  // ever reaches it.
  const mutation = useMutation({
    mutationFn: () => {
      const paymentDate = calendarDateInputToIso(date);
      if (!paymentDate) throw new Error("Enter a payment date.");
      return commissionsApi.adjustment(claim.summary.id, {
        adjustmentType: type,
        amount: amountNum,
        adjustmentReason: reason.trim(),
        paymentDate,
        paymentMethod: method,
        referenceNumber: reference.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      // WS7 — same cross-prefix invalidation as RecordPaymentModal so
      // the admin payment ledger tiles + rows refresh.
      qc.invalidateQueries({ queryKey: commissionKeys.adminPayments() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Post adjustment — ${claim.summary.claimNumber}`}
      description="Adjustments are immutable once posted. Every row is audited."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Post adjustment
          </Button>
        </>
      }
    >
      {error && (
        <div
          role="alert"
          className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3"
        >
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-space-4">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Adjustment type
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-2">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={
                  "text-left p-space-3 rounded-md border transition-colors duration-fast " +
                  (type === t.value
                    ? "border-gold bg-gold/10"
                    : "border-border-light bg-white-pure hover:border-gold")
                }
              >
                <div className="font-display text-label font-semibold uppercase tracking-label text-onyx">
                  {t.label}
                </div>
                <div className="text-caption text-gray-mid mt-space-1">{t.blurb}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField
            label="Amount"
            required
            helperText={selected.blurb}
            error={subCent ? WHOLE_CENTS_MESSAGE : undefined}
          >
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              invalid={subCent}
            />
          </FormField>
          <FormField label="Date" required>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </FormField>
          <FormField label="Method" required>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Reference" required>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </FormField>
        </div>

        <FormField
          label="Reason"
          required
          helperText="Immutable and shown to the claimant and in the audit trail."
        >
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>
      </div>
    </Modal>
  );
}
